import Search from "./Top/Search";
import SiteSelect from "./Top/SiteSelect";
import ItemList from './Content/ItemList/ItemList';
import Tree from './Tree/Tree';
import TopMenu from './Content/TopMenu/TopMenu';
import {PathPickerType} from '../../types/enums';
import {Dispatch} from 'redux';
import {Breadcrumb, Item, ModalProps, ModalState, SelectableSite} from '../../types/types';
import {
  fetchBreadcrumbs,
  fetchChildrenByFilter,
  fetchFolderChildren,
  fetchPreferences,
  fetchSites,
  fetchTreeChildren,
  search
} from '../../actions';
import {Factory} from '../../utils/Factory';
import {path} from 'ramda';
import {searchQueryIsNotEmpty} from '../../utils/searchQuery';
import {ContentContext, TreeContext} from "../../utils/contexts";

const {
  ModalTest: {
    default: EdocatModal,
    actions: { toggleModal },
  },
  Button,
} = window.Edocat.Components;

class Modal extends window.React.PureComponent<ModalProps, ModalState> {
  constructor(props: ModalProps) {
    super(props);

    this.state = Factory.GET_INITIAL_STATE(props.selected || {});
  }

  async componentDidMount() {
    const { multiSelect, selected, site } = this.props;

    this.fetchPreferences();

    const sites = await fetchSites();
    let selectedSite = sites[site] || Factory.GET_EMPTY_SITE();

    //If only one item should be selected and there is already one selected item, display its parent folder content.
    if (!multiSelect && selected && Object.keys(selected).length === 1) {
      await this.fetchBreadcrumbs(Object.keys(selected)[0], true);

      const currentBreadcrumbs = this.state.breadcrumbs;

      if (currentBreadcrumbs.length > 0) {
        const parent = currentBreadcrumbs[currentBreadcrumbs.length - 1];

        selectedSite = sites[currentBreadcrumbs[0].name] || Factory.GET_EMPTY_SITE();

        await Promise.all([this.fetchContent(parent.nodeRef), this.fetchTreeChildren(parent.nodeRef)]);
      }
    } else if (selectedSite.nodeRef) {
      await this.onSiteSelect(selectedSite);
    }

    this.setState({
      sites,
      currentSite: selectedSite,
    });
  }

  onSiteSelect = async (site: SelectableSite) => {
    const { searchQuery } = this.state;

    if (site) {
      if (searchQueryIsNotEmpty(searchQuery)) {
        await this.doSearch(searchQuery, site?.nodeRef);
      } else {
        await this.fetchContent(site.nodeRef, 1);
      }

      await this.fetchTreeChildren(site.nodeRef);

      this.setState({
        currentSite: site,
        breadcrumbs: [{ name: site.label, nodeRef: site.nodeRef }],
      });
    } else {
      this.setState({
        content: [],
        page: 1,
        total: 0,
        treeItems: {},
        filter: '',
        currentSite: Factory.GET_EMPTY_SITE(),
        breadcrumbs: [],
      });
    }
  };

  onBreadcrumbClick = async (breadcrumb: Breadcrumb) => {
    const { searchQuery } = this.state;

    if (searchQueryIsNotEmpty(searchQuery)) {
      await this.doSearch(searchQuery, breadcrumb.nodeRef);
    } else {
      await this.fetchContent(breadcrumb.nodeRef, 1);
    }

    this.setState(oldState => ({
      breadcrumbs: [...oldState.breadcrumbs.slice(0, oldState.breadcrumbs.indexOf(breadcrumb) + 1)],
    }));
  };

  onItemSelect = (item: Item) => {
    const { type, multiSelect } = this.props;
    const { selected } = this.state;

    if (type === PathPickerType.ALL || type === item.type) {
      let newSelected = {
        ...selected,
      };

      if (selected[item.nodeRef]) {
        delete newSelected[item.nodeRef];
      } else {
        if (multiSelect) {
          newSelected[item.nodeRef] = item.nodeRef;
        } else {
          newSelected = {
            [item.nodeRef]: item.nodeRef,
          };
        }
      }

      this.setState({
        selected: newSelected,
      });
    }
  };

  //Used in case of search result folder open

  // onFolderOpen = async (item: Item) => {
  //   const { nodeRef, name, path } = item;
  //   const { searchQuery, selected, breadcrumbs } = this.state;
  //   const { type, multiSelect } = this.props;
  //
  //   const { items, total } = await fetchFolderChildren(nodeRef, type);
  //
  //   const isSearchResult: boolean = searchQueryIsNotEmpty(searchQuery);
  //
  //   //In single select mode, choose the opened folder, if it is empty.
  //   const newSelected = multiSelect ? selected : items.length === 0 ? { [item.nodeRef]: item.nodeRef } : {};
  //
  //   //If the opened folder is a search result item, set its path to breadcrumbs.
  //   const newBreadcrumbs = isSearchResult
  //     ? [...(path || []), { name, nodeRef }]
  //     : [...breadcrumbs, { name, nodeRef }];
  //
  //   this.setState(oldState => ({
  //     content: items,
  //     selected: newSelected,
  //     breadcrumbs: newBreadcrumbs,
  //     //If the opened folder is a search result item, treat search finished: stop showing paths and clear search query
  //     searchMode: isSearchResult ? false : oldState.searchMode,
  //     searchQuery: isSearchResult ? '' : oldState.searchQuery,
  //     total,
  //   }));
  // };

  doSearch = async (searchQuery: string, nodeRef: string = '', skipCount: number = 0, maxItems: number = this.state.pageSize) => {
    const { type } = this.props;
    const { breadcrumbs } = this.state;

    const rootNodeRef: string = nodeRef || breadcrumbs[breadcrumbs.length - 1]?.nodeRef;

    this.setState({
      loadingContent: true,
    });

    const { items, total } = await search(searchQuery, rootNodeRef, type, skipCount, maxItems);

    this.setState(() => ({
      content: items,
      searchMode: true,
      total,
      loadingContent: false,
    }));
  };

  fetchContent = async (nodeRef: string = '', page: number = this.state.page, sort: boolean = this.state.sort, sortByValue: string = this.state.sortByValue, pageSize: number = this.state.pageSize) => {
    const { selected, breadcrumbs } = this.state;
    const { multiSelect, type } = this.props;

    const rootNodeRef: string = nodeRef || breadcrumbs[breadcrumbs.length - 1]?.nodeRef;

    this.setState({
      loadingContent: true,
    });

    const { items, total, isSiteRoot } = await fetchFolderChildren(rootNodeRef, type, page, sort, sortByValue, pageSize);

    //In single select folder mode, choose the opened folder, if it is empty and is not site root.
    const newSelected = !isSiteRoot && !multiSelect && type === PathPickerType.FOLDER && total === 0 ? { [nodeRef]: nodeRef } : selected;

    this.setState(() => ({
      content: items,
      searchMode: false,
      total,
      loadingContent: false,
      selected: newSelected,
    }));
  };

  fetchChildrenByFilter = async (filter: string) => {
    const { currentSite, sort, sortByValue, pageSize } = this.state;
    const { type } = this.props;

    const { items, total } = await fetchChildrenByFilter(currentSite.value, filter, type, 1, sort, sortByValue, pageSize);

    this.setState(() => ({
      content: items,
      searchMode: false,
      total,
    }));
  };

  fetchTreeChildren = async (nodeRef: string = '') => {
    const { breadcrumbs } = this.state;

    const rootNodeRef: string = nodeRef || breadcrumbs[breadcrumbs.length - 1]?.nodeRef;

    this.setState({
      loadingItem: rootNodeRef,
    });

    const newTreeItems = await fetchTreeChildren(rootNodeRef);

    this.setState(state => ({
      treeItems: {
        ...state.treeItems,
        ...newTreeItems,
      },
      loadingItem: '',
    }));
  };

  fetchBreadcrumbs = async (nodeRef: string, parentPath: boolean = false) => {
    const { sites } = this.state;

    let breadcrumbs = await fetchBreadcrumbs(nodeRef, parentPath);

    if (breadcrumbs.length > 0 && sites[breadcrumbs[0].name]) {
      breadcrumbs = [{
        nodeRef: breadcrumbs[0].nodeRef,
        name: sites[breadcrumbs[0].name].label,
      }, ...breadcrumbs.slice(1)];
    }

    this.setState({
      breadcrumbs,
    });
  };

  fetchPreferences = async () => {
    if (window.Alfresco?.constants?.USERNAME) {
      this.setState(() => ({
        loadingContent: true,
      }));

      const { favouriteDocuments, favouriteFolders } = await fetchPreferences();

      this.setState({
        favouriteDocuments,
        favouriteFolders,
        loadingContent: false,
      });
    }
  };

  setProperty = (name: string, value: any) => {
    //@ts-ignore
    this.setState({
      [name]: value,
    });
  }

  onSubmit = () => {
    const { selected } = this.state;
    const { onSubmit } = this.props;

    const selectedArr = Object.keys(selected);

    if (selectedArr.length > 0 && onSubmit) {
      onSubmit(selectedArr);
      this.onClose();
    }
  };

  onClose = () => {
    const { formInstanceId, actions, name } = this.props;

    actions.toggleModal(false, name, null, formInstanceId);
  };

  render() {
    const { sites, currentSite, content, breadcrumbs, searchQuery, selected, treeItems, loadingItem, sort, sortByValue, page, total, pageSize, filter, searchMode, loadingContent, favouriteDocuments, favouriteFolders, } = this.state;
    const { type, reducerPath, name, multiSelect, hidden, title, initialText } = this.props;

    const contentContext = {
      sites,
      breadcrumbs,
      content,
      onBreadcrumbClick: this.onBreadcrumbClick,
      onItemSelect: this.onItemSelect,
      selected,
      fetchContent: this.fetchContent,
      fetchChildrenByFilter: this.fetchChildrenByFilter,
      fetchTreeChildren: this.fetchTreeChildren,
      fetchBreadcrumbs: this.fetchBreadcrumbs,
      setProperty: this.setProperty,
      doSearch: this.doSearch,
      fetchPreferences: this.fetchPreferences,
      searchQuery,
      sort,
      sortByValue,
      page,
      total,
      pageSize,
      filter,
      type,
      multiSelect,
      currentSite,
      searchMode,
      hidden,
      loadingContent,
      favouriteDocuments,
      favouriteFolders,
      initialText,
    };

    const treeContext = {
      treeItems,
      currentSite,
      fetchContent: this.fetchContent,
      fetchTreeChildren: this.fetchTreeChildren,
      fetchBreadcrumbs: this.fetchBreadcrumbs,
      selected,
      loadingItem,
      breadcrumbs,
      searchMode,
    };

    const standardTitle =
      window.Alfresco.messages.global[
        `edocat.global.path-picker.select-${type === PathPickerType.FOLDER ? 'folder' : 'file'}`
        ];

    const modalState = reducerPath ? path([...reducerPath, name], this.props) : this.props.modal[name];

    return (
      <EdocatModal
        name={name}
        title={title || standardTitle}
        className="PathPicker-Modal"
        hideActionButtons
        onClose={this.onClose}
        reducerPath={reducerPath}
        fullWidth
      >
        <div className="modal-body" id="path-picker-body">
          <div className="Top">
            <SiteSelect
              onSiteSelect={this.onSiteSelect}
              currentSite={currentSite}
              loadingContent={loadingContent}
              sites={sites}
            />
            <Search
              fetchContent={this.fetchContent}
              doSearch={this.doSearch}
              searchQuery={searchQuery}
              setProperty={this.setProperty}
              loadingContent={loadingContent}
              type={type}
            />
          </div>
          <div className="Bottom">
            {currentSite.nodeRef && (
              <div className="Tree">
                <TreeContext.Provider value={treeContext}>
                  <Tree />
                </TreeContext.Provider>
              </div>
            )}
            <div className="Content">
              <ContentContext.Provider value={contentContext}>
                {((!searchMode && currentSite.nodeRef) || (searchMode && content.length > 0)) && <TopMenu />}
                <ItemList />
              </ContentContext.Provider>
            </div>
          </div>
        </div>
        <div className="modal-actions">
          <Button
            primary
            label={modalState?.items?.okLabel || window.Alfresco.messages.global['button.ok']}
            onClick={this.onSubmit}
            disabled={Object.keys(selected).length === 0}
          />
          <Button
            label={modalState?.items?.cancelLabel || window.Alfresco.messages.global['button.close']}
            onClick={this.onClose}
          />
        </div>
      </EdocatModal>
    );
  }
}

const mapStateToProps = (state: any) => ({
  ...state,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: window.redux.bindActionCreators({ toggleModal }, dispatch),
});

export default window.reactRedux.connect(mapStateToProps, mapDispatchToProps)(Modal);
